/**
 * Como a documentação agrupa e descreve os tokens de cor.
 *
 * Aqui só há NOME de token e texto de uso. Nenhum valor: a cor de cada token,
 * em cada tema, é lida de `ui/estilos/tokens.css` na hora de mostrar
 * (`CoresTab.tsx`), e a razão de contraste é medida por
 * `ui/estilos/contraste.ts`, a mesma conta que o build confere. Valor escrito
 * aqui envelheceria no primeiro ajuste de `tokens.css` — e já envelheceu uma
 * vez: a documentação mostrava para `--warning` um valor e uma razão que o
 * arquivo já não tinha.
 *
 * `AdminDesignSystem.spec.tsx` confere que todo token de cor
 * de `:root` está em algum grupo, e que nenhum grupo cita token que não existe.
 */

export interface EntradaDaPaleta {
  token: string;
  nome: string;
  uso: string;
}

export interface GrupoDaPaleta {
  id: string;
  titulo: string;
  descricao: string;
  entradas: readonly EntradaDaPaleta[];
}

/**
 * Referência Pantone das cores institucionais. `tokens.css` a guarda em
 * comentário, que o leitor de tokens não lê; por isso o mapa, pequeno e só de
 * nome — o valor continua vindo do arquivo.
 */
export const PANTONE: Readonly<Record<string, string>> = {
  primary: 'Azul Institucional, Pantone 654 C',
  orange: 'Laranja Institucional, Pantone 021 C',
  destructive: 'Vermelho, Pantone 485 C',
};

export const GRUPOS_DA_PALETA: readonly GrupoDaPaleta[] = [
  {
    id: 'institucionais',
    titulo: 'Cores institucionais',
    descricao:
      'Padrão da Prefeitura de São Paulo, Manual de Identidade Visual 2025. No tema escuro e no alto contraste a luminosidade muda para manter o contraste; a matiz é a mesma.',
    entradas: [
      {
        token: 'primary',
        nome: 'Primária',
        uso: 'Preenchimento do botão primário e ícones principais. Como texto sobre o fundo, use --link.',
      },
      {
        token: 'primary-foreground',
        nome: 'Texto sobre a primária',
        uso: 'Rótulo do botão primário.',
      },
      {
        token: 'orange',
        nome: 'Destaque',
        uso: 'Marcador do item ativo da barra lateral. Não é cor de texto nem fundo do número do sino.',
      },
      {
        token: 'orange-foreground',
        nome: 'Texto sobre o destaque',
        uso: 'Texto sobre o laranja. O número do sino usa --destructive-foreground sobre --destructive.',
      },
      {
        token: 'destructive',
        nome: 'Destrutiva',
        uso: 'Fundo do botão destrutivo, do selo de erro e do número do sino. Como texto, use --destructive-text.',
      },
      {
        token: 'destructive-foreground',
        nome: 'Texto sobre a destrutiva',
        uso: 'Rótulo do botão destrutivo e número do sino de notificações.',
      },
    ],
  },
  {
    id: 'semanticas',
    titulo: 'Cores semânticas',
    descricao:
      'Situação de um registro. Cada uma tem a cor de FUNDO de selo (texto claro por cima) e, quando aparece como texto num cartão, o token -text, porque os dois usos puxam a luminosidade para lados opostos.',
    entradas: [
      { token: 'success', nome: 'Sucesso', uso: 'Fundo do selo de situação positiva.' },
      { token: 'success-foreground', nome: 'Texto sobre sucesso', uso: 'Rótulo do selo.' },
      {
        token: 'success-text',
        nome: 'Sucesso como texto',
        uso: 'Rótulo de situação positiva sobre o cartão.',
      },
      { token: 'warning', nome: 'Atenção', uso: 'Fundo do selo de pendência ou alerta.' },
      { token: 'warning-foreground', nome: 'Texto sobre atenção', uso: 'Rótulo do selo.' },
      {
        token: 'warning-text',
        nome: 'Atenção como texto',
        uso: 'Rótulo de pendência sobre o cartão e sobre o fundo tingido de 5%.',
      },
      { token: 'info', nome: 'Informação', uso: 'Fundo do selo informativo.' },
      { token: 'info-foreground', nome: 'Texto sobre informação', uso: 'Rótulo do selo.' },
      {
        token: 'destructive-text',
        nome: 'Erro como texto',
        uso: 'Mensagem de erro dos campos e texto do aviso de erro, sobre o cartão e sobre o fundo.',
      },
    ],
  },
  {
    id: 'neutras',
    titulo: 'Cores neutras',
    descricao: 'Fundo, superfícies, texto e bordas da interface.',
    entradas: [
      { token: 'background', nome: 'Fundo', uso: 'Fundo geral da aplicação.' },
      { token: 'foreground', nome: 'Texto', uso: 'Texto principal sobre o fundo.' },
      { token: 'card', nome: 'Cartão', uso: 'Fundo de cartão e painel.' },
      { token: 'card-foreground', nome: 'Texto no cartão', uso: 'Texto principal no cartão.' },
      { token: 'popover', nome: 'Menu suspenso', uso: 'Fundo de menu, popover e dica.' },
      { token: 'popover-foreground', nome: 'Texto no menu', uso: 'Texto de menu e popover.' },
      { token: 'secondary', nome: 'Secundária', uso: 'Fundo do botão secundário.' },
      {
        token: 'secondary-foreground',
        nome: 'Texto sobre a secundária',
        uso: 'Rótulo do botão secundário.',
      },
      { token: 'muted', nome: 'Apagada', uso: 'Fundo secundário e campo desabilitado.' },
      {
        token: 'muted-foreground',
        nome: 'Texto secundário',
        uso: 'Rótulo, descrição e texto auxiliar.',
      },
      {
        token: 'accent',
        nome: 'Realce',
        uso: 'Fundo de hover do botão contorno ou fantasma e do item realçado de menu. Não é o laranja de destaque.',
      },
      { token: 'accent-foreground', nome: 'Texto sobre o realce', uso: 'Texto do item realçado.' },
      { token: 'border', nome: 'Borda', uso: 'Borda de cartão e separador.' },
      { token: 'input', nome: 'Borda de campo', uso: 'Borda de campo de formulário.' },
    ],
  },
  {
    id: 'especiais',
    titulo: 'Cores especiais',
    descricao:
      'Tokens com regra própria: o véu e o vidro escuro não acompanham o tema, e a cor do gov.br é marca de terceiro.',
    entradas: [
      {
        token: 'link',
        nome: 'Ligação',
        uso: 'Texto de link sobre o fundo e sobre o cartão. No alto contraste, toda ligação é pintada com ele.',
      },
      {
        token: 'ring',
        nome: 'Anel de foco',
        uso: 'Contorno de foco do teclado (Azul Sinalização, Pantone 3005 C).',
      },
      {
        token: 'overlay',
        nome: 'Vidro escuro',
        uso: 'Atrás de diálogo, gaveta e folha, sempre com opacidade (bg-overlay/80).',
      },
      {
        token: 'hero-overlay',
        nome: 'Véu do hero',
        uso: 'Por cima da fotografia do portal, em degradê de 95% a 70%.',
      },
      {
        token: 'hero-overlay-foreground',
        nome: 'Texto no véu do hero',
        uso: 'Título e texto do hero do portal.',
      },
      { token: 'govbr', nome: 'gov.br', uso: 'Botão "Entrar com gov.br", cor da marca gov.br.' },
      { token: 'govbr-foreground', nome: 'Texto no botão gov.br', uso: 'Rótulo e ícone do botão.' },
    ],
  },
  {
    id: 'barra-lateral',
    titulo: 'Barra lateral',
    descricao: 'Cores da BarraLateral das áreas internas, derivadas do Azul Institucional.',
    entradas: [
      { token: 'sidebar-background', nome: 'Fundo', uso: 'Fundo da barra lateral.' },
      { token: 'sidebar-foreground', nome: 'Texto', uso: 'Rótulo dos itens.' },
      { token: 'sidebar-primary', nome: 'Primária', uso: 'Elemento principal da barra.' },
      {
        token: 'sidebar-primary-foreground',
        nome: 'Texto sobre a primária',
        uso: 'Texto sobre o elemento principal.',
      },
      { token: 'sidebar-accent', nome: 'Item ativo', uso: 'Fundo do item ativo e do hover.' },
      {
        token: 'sidebar-accent-foreground',
        nome: 'Texto do item ativo',
        uso: 'Rótulo do item ativo.',
      },
      { token: 'sidebar-border', nome: 'Borda', uso: 'Separadores internos.' },
      { token: 'sidebar-ring', nome: 'Anel de foco', uso: 'Foco do teclado dentro da barra.' },
    ],
  },
  {
    id: 'graficos',
    titulo: 'Séries de gráfico',
    descricao:
      'Na ordem em que entram (ui/charts/chartTheme.tsx). São elemento gráfico: o mínimo é 3:1 sobre o cartão. Rótulo, legenda e dica usam a cor de texto.',
    entradas: [1, 2, 3, 4, 5, 6].map((n) => ({
      token: `chart-${n}`,
      nome: `Série ${n}`,
      uso: n === 6 ? 'Roxo, a única série sem token institucional de origem.' : 'Série de gráfico.',
    })),
  },
];
